import React, { useState, useEffect } from 'react';

// Processing steps to show users what's happening
const PROCESSING_STEPS = [
  'Processing input variables...',
  'Testing income reasonableness...',
  'Analyzing local land sales...',
  'Designing cost table...',
  'Calculating replacement costs...',
  'Estimating depreciation...',
  'Evaluating market conditions...',
  'Analyzing comparable sales...',
  'Processing regression analysis...',
  'Determining asset valuations...',
  'Post interpretive risk assessments...',
  'Validating...',
  'Finalizing valuation report...'
];

// Sample sponsor/affiliate ads - replace with your actual ads
const SPONSOR_ADS = [
  {
    id: 1,
    title: 'Premium Fuel Solutions',
    description: 'Get the best wholesale fuel prices in your area',
    image: '/ads/fuel-supplier.jpg',
    link: '#'
  },
  {
    id: 2,
    title: 'Business Insurance Experts',
    description: 'Protect your gas station with comprehensive coverage',
    image: '/ads/insurance.jpg',
    link: '#'
  },
  {
    id: 3,
    title: 'Point of Sale Systems',
    description: 'Modern POS solutions for convenience stores',
    image: '/ads/pos-system.jpg',
    link: '#'
  }
];

export default function LoadingScreen() {
  const [progress, setProgress] = useState(0);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Progress bar animation - fills up over 90 seconds
  useEffect(() => {
    const duration = 90000; // 90 seconds
    const intervalTime = 100; // Update every 100ms
    const increment = (100 / duration) * intervalTime;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        // Cap at 95% until actual completion
        return next >= 95 ? 95 : next;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  // Rotate processing steps every 7 seconds
  useEffect(() => {
    const stepTimer = setInterval(() => {
      setCurrentStepIndex((prev) => (prev + 1) % PROCESSING_STEPS.length);
    }, 7000);

    return () => clearInterval(stepTimer);
  }, []);

  // Rotate ads every 10 seconds
  useEffect(() => {
    const adTimer = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % SPONSOR_ADS.length);
    }, 10000);

    return () => clearInterval(adTimer);
  }, []);

  const currentAd = SPONSOR_ADS[currentAdIndex];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Main Loading Content */}
        <div className="p-8">
          <div className="flex flex-col items-center">
            {/* Spinner */}
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-fc-orange-500 mb-4"></div>

            {/* Title */}
            <h3 className="text-xl font-semibold mb-2">Analyzing Property Data</h3>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6">
              Our AI is processing your property details and financial metrics to generate an accurate valuation.
              This typically takes 60-90 seconds as our model analyzes your data.
            </p>

            {/* Progress Bar */}
            <div className="w-full mb-2">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-fc-orange-500 h-3 rounded-full transition-all duration-300 ease-linear"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Progress Text */}
            <div className="flex justify-between w-full mb-6">
              <p className="text-xs text-gray-500">{PROCESSING_STEPS[currentStepIndex]}</p>
              <p className="text-xs text-gray-500 font-semibold">{Math.round(progress)}%</p>
            </div>
          </div>
        </div>

        {/* Advertisement Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-gray-200 p-6">
          <p className="text-xs text-gray-500 text-center mb-3">Sponsored by our partners</p>

          <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-4">
                {/* Ad Image Placeholder */}
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">📊</span>
                </div>

                {/* Ad Content */}
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 mb-1">{currentAd.title}</h4>
                  <p className="text-sm text-gray-600">{currentAd.description}</p>
                </div>
              </div>
          </div>

          {/* Ad Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {SPONSOR_ADS.map((ad, index) => (
              <div
                key={ad.id}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentAdIndex
                    ? 'w-6 bg-fc-orange-500'
                    : 'w-1.5 bg-gray-300'
                }`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
